import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FileCheck, Download, Eye, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { ScrollArea } from "@/components/ui/scroll-area";

interface SignedProposal {
  id: string;
  proposal_name: string;
  client_name: string;
  client_title: string;
  signed_at: string;
  signature_data: string;
  proposal_content: any;
}

interface SignedProposalsDialogProps {
  onViewProposal: (proposal: SignedProposal) => void;
  onExportSignedPDF: (proposal: SignedProposal) => void;
}

export const SignedProposalsDialog = ({ onViewProposal, onExportSignedPDF }: SignedProposalsDialogProps) => {
  const [open, setOpen] = useState(false);
  const [proposals, setProposals] = useState<SignedProposal[]>([]);
  const [loading, setLoading] = useState(false);
  const [exportingId, setExportingId] = useState<string | null>(null);
  const { toast } = useToast();

  const loadSignedProposals = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("signed_proposals")
        .select("*")
        .eq("creator_user_id", user.id)
        .order("signed_at", { ascending: false });

      if (error) throw error;

      setProposals(data || []);
    } catch (error: any) {
      console.error("Error loading signed proposals:", error);
      toast({
        title: "Error",
        description: "Failed to load signed proposals",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadSignedProposals();
    }
  }, [open]);

  const handleExportPDF = async (proposal: SignedProposal) => {
    setExportingId(proposal.id);
    try {
      await onExportSignedPDF(proposal);
    } finally {
      setExportingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="bg-blue-600 hover:bg-blue-700">
          <FileCheck className="w-4 h-4 mr-2" />
          <span className="text-xs sm:text-sm">Signed</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Signed Proposals</DialogTitle>
        </DialogHeader>
        
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : proposals.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <FileCheck className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>No signed proposals yet</p>
          </div>
        ) : (
          <ScrollArea className="h-[500px] pr-4">
            <div className="space-y-3">
              {proposals.map((proposal) => (
                <div
                  key={proposal.id}
                  className="border rounded-lg p-4 hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-slate-800 truncate">
                        {proposal.proposal_name}
                      </h3>
                      <div className="text-sm text-slate-600 mt-1">
                        <p>Client: <span className="font-medium">{proposal.client_name}</span></p>
                        {proposal.client_title && (
                          <p>Title: {proposal.client_title}</p>
                        )}
                        <p className="text-xs text-slate-500 mt-1">
                          Signed: {format(new Date(proposal.signed_at), "PPpp")}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          onViewProposal(proposal);
                          setOpen(false);
                        }}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleExportPDF(proposal)}
                        disabled={exportingId === proposal.id}
                      >
                        {exportingId === proposal.id ? (
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4 mr-1" />
                        )}
                        PDF
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
};
